import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Sparkle, Translate, ArrowCounterClockwise } from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";

// Live AI suggestion shown above the composer while staff type.
export const LiveSuggestion = ({ draft, conversationId, onUse }) => {
  const [sugg, setSugg] = useState("");
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    const t = draft.trim();
    setSugg("");
    if (t.length < 12) return;
    let live = true;
    const h = setTimeout(() => {
      setLoading(true);
      api.post("/chat/assist/complete", { text: t, conversation_id: conversationId })
        .then(r => { if (live && r.data.suggestion && r.data.suggestion.trim() !== t) setSugg(r.data.suggestion); })
        .catch(() => {})
        .finally(() => live && setLoading(false));
    }, 1200);
    return () => { live = false; clearTimeout(h); };
  }, [draft, conversationId]);
  if (!sugg && !loading) return null;
  return (
    <div className="mb-2 flex items-start gap-2 px-3 py-2 rounded-lg bg-[#EEF2FB] border border-[#D3DCF0] text-xs text-[#0B2B66]" data-testid="chat-live-suggestion">
      <Sparkle size={14} weight="fill" className="mt-0.5 shrink-0" />
      <span className="flex-1">{loading && !sugg ? "Thinking…" : sugg}</span>
      {sugg && (
        <button type="button" onClick={() => { onUse(sugg); setSugg(""); }} data-testid="chat-live-suggestion-use"
                className="shrink-0 h-6 px-2 rounded bg-[#0B2B66] text-white font-semibold">Use</button>
      )}
    </div>
  );
};

// English → Malayalam for the outgoing draft, with undo.
export const TranslateDraftButton = ({ draft, setDraft, client = api, path = "/chat/assist/translate" }) => {
  const [busy, setBusy] = useState(false);
  const [prev, setPrev] = useState(null);
  if (prev !== null) {
    return (
      <button type="button" onClick={() => { setDraft(prev); setPrev(null); }} data-testid="chat-translate-undo"
              title="Back to English" className="h-10 w-10 rounded-full border border-[#B45309] text-[#B45309] hover:bg-[#FFFBEB] flex items-center justify-center shrink-0">
        <ArrowCounterClockwise size={16} />
      </button>
    );
  }
  const run = async () => {
    if (!draft.trim()) return;
    setBusy(true);
    try {
      const r = await client.post(path, { text: draft, target: "ml" });
      setPrev(draft); setDraft(r.data.text);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <button type="button" onClick={run} disabled={busy || !draft.trim()} data-testid="chat-translate-ml"
            title="Translate to Malayalam" className="h-10 px-2.5 rounded-full border border-[#B45309] text-[#B45309] hover:bg-[#FFFBEB] disabled:opacity-50 inline-flex items-center gap-1 text-[11px] font-bold shrink-0">
      <Translate size={14} className={busy ? "animate-pulse" : ""} /> മല
    </button>
  );
};

const LANG = { en: ["Translate to English", "EN"], ml: ["Translate to Malayalam", "മല"] };

// Translate a chat bubble (cached per message + language on the server).
export const TranslateMessage = ({ m, targets = ["en", "ml"], client = api, path = "/chat/assist/translate", tone = "text-[#0B2B66]" }) => {
  const [out, setOut] = useState(() => Object.fromEntries(targets.filter(t => m[`translation_${t}`]).map(t => [t, m[`translation_${t}`]])));
  const [busy, setBusy] = useState("");
  const run = async (target) => {
    setBusy(target);
    try {
      const r = await client.post(path, { text: m.text, target, message_id: m.id });
      setOut(o => ({ ...o, [target]: r.data.text }));
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  };
  return (
    <div className="mt-1">
      {targets.filter(t => out[t]).map(t => (
        <div key={t} className={`pt-1 border-t border-current/20 text-[12px] italic ${tone}`} data-testid={`chat-translation-${t}-${m.id}`}>{LANG[t][1]}: {out[t]}</div>
      ))}
      <div className="flex gap-2">
        {targets.filter(t => !out[t]).map(t => (
          <button key={t} type="button" onClick={() => run(t)} disabled={!!busy} data-testid={`chat-translate-${t}-${m.id}`}
                  className={`text-[10px] font-semibold underline disabled:opacity-50 ${tone}`}>
            {busy === t ? "Translating…" : LANG[t][0]}
          </button>
        ))}
      </div>
    </div>
  );
};

export const TranslateIncoming = ({ m }) => <TranslateMessage m={m} />;
